import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export const MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "queue", label: "My Queue" },
  { key: "jobs", label: "All Jobs / Job Cards" },
  { key: "sales", label: "Sales (New Order, Prices & Payments)" },
  { key: "production", label: "Production Board" },
  { key: "customers", label: "Customers" },
  { key: "enquiries", label: "Enquiries" },
  { key: "products", label: "Product Catalog" },
  { key: "agents", label: "Rozfa Agents" },
  { key: "stock", label: "Stock / Inventory" },
  { key: "attendance", label: "Attendance (manage: records, leave, roster)" },
  { key: "reports", label: "Reports" },
  { key: "settings", label: "Rozfa Staff" },
  { key: "system", label: "System Settings (full control)" },
];

// ---- Dynamic system config (mutable caches, refreshed via loadConfig()) ----
export const STATUSES = ["To Confirm", "Payment Received", "Reviewing", "On Hold", "Printing", "Preparing", "Ready for Pick-Up", "Collected", "Delivery Arranged", "Cancelled"];
export const DONE_STATUSES = ["Collected", "Cancelled"];
export const STATUS_META = {};
export const DEPT_LABELS = { sales: "Sales / CS", finance: "Finance", design: "Designer", production: "Production", qc: "QC / Packing", delivery: "Delivery / Runner" };
export const DEPARTMENTS = Object.keys(DEPT_LABELS).map((key) => ({ key, label: DEPT_LABELS[key], statuses: [] }));
export const CONFIG = { company: { name: "Rozfa Printing", whatsapp: "" }, tiers: [], qc_items: [], wa_templates: {}, job_number: {}, scan_rules: { designer: ["Reviewing Customer's File", "Printing"], production: ["Printing", "Finishing", "Preparing & Packing"], qc_packing: ["Preparing & Packing", "Ready for Pick-Up"] } };

const applyStatuses = (list) => {
  STATUSES.splice(0, STATUSES.length, ...list.map((s) => s.name));
  DONE_STATUSES.splice(0, DONE_STATUSES.length, ...list.filter((s) => s.done).map((s) => s.name));
  Object.keys(STATUS_META).forEach((k) => delete STATUS_META[k]);
  list.forEach((s) => { STATUS_META[s.name] = s; });
  DEPARTMENTS.forEach((d) => { d.statuses = list.filter((s) => s.dept === d.key).map((s) => s.name); });
};

export const loadConfig = async () => {
  try {
    const { data } = await api.get("/config");
    if (data.statuses) applyStatuses(data.statuses);
    if (data.process_times) PROCESS_TIMES.splice(0, PROCESS_TIMES.length, ...data.process_times);
    Object.assign(CONFIG, data);
  } catch { /* keep defaults */ }
  return CONFIG;
};

export const fillTemplate = (tpl, vars) => String(tpl || "").replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? ""));

const PT_COLORS = [
  "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950/70 dark:text-sky-300 dark:border-sky-800",
  "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/70 dark:text-amber-300 dark:border-amber-800",
  "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-950/70 dark:text-orange-300 dark:border-orange-800",
  "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950/70 dark:text-rose-300 dark:border-rose-800",
  "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300 dark:bg-fuchsia-950/70 dark:text-fuchsia-300 dark:border-fuchsia-800",
  "bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-950/70 dark:text-violet-300 dark:border-violet-800",
];
export const ptColor = (i) => PT_COLORS[i % PT_COLORS.length];

// mutable cache, refreshed via loadProcessTimes()
export const PROCESS_TIMES = [
  { value: "Normal Job", turnaround: "3 Days", surcharge: 0, hours: 72 },
  { value: "24 Hours", turnaround: "24 Hours", surcharge: 20, hours: 24 },
  { value: "Urgent", turnaround: "Same Day", surcharge: 30, hours: 0 },
  { value: "Super Urgent", turnaround: "6 Hours", surcharge: 60, hours: 0 },
];

export const loadProcessTimes = async () => {
  try {
    const { data } = await api.get("/process-times");
    PROCESS_TIMES.splice(0, PROCESS_TIMES.length, ...data);
  } catch (e) { console.warn("loadProcessTimes failed, keeping defaults", e); }
  return PROCESS_TIMES;
};

export const jobPic = (j) => j?.current_pic_name || j?.pic_name || "-";

export const statusColor = (s) => STATUS_META[s]?.color || "#e2e8f0";

export const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "customer_service", label: "Customer Service" },
  { value: "sales", label: "Sales" },
  { value: "designer", label: "Designer" },
  { value: "production", label: "Production" },
  { value: "qc_packing", label: "QC / Packing" },
  { value: "finance", label: "Finance" },
  { value: "runner", label: "Runner / Installer" },
  { value: "manager", label: "Manager" },
];

export const AGENT_ROLE = { value: "agent", label: "Rozfa Agents" };

export const formatRM = (v) => {
  const n = Number(v || 0);
  return "RM " + n.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const waPhone = (phone) => {
  let p = String(phone || "").replace(/\D/g, "");
  if (p.startsWith("0")) p = "6" + p;
  return p;
};

export const waLink = (phone, message) =>
  `https://wa.me/${waPhone(phone)}${message ? `?text=${encodeURIComponent(message)}` : ""}`;

export const waVars = (job = {}) => ({ ...job, company: CONFIG.company?.name || "Rozfa Printing", agent_company: job.agent_company ? ` from ${job.agent_company}` : "" });
export const waJobMessage = (job) => fillTemplate(CONFIG.wa_templates?.customer_status || `Hi {customer_name}, this is {company}. Update for job {job_number} ({product}): current status is "{status}". Thank you!`, waVars(job));
export const waAgentJobMessage = (job) => fillTemplate(CONFIG.wa_templates?.agent_job || "Hi {agent_name}, this is {company} regarding job {job_number} ({product}) for {customer_name}.", waVars(job));

export const formatDate = (s) => {
  if (!s) return "-";
  try {
    const d = new Date(s);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
};

export const formatDateTime = (s) => {
  if (!s) return "-";
  try {
    const d = new Date(s);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
};
